import * as React from "react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Spinner } from "@fluentui/react-components";
import { apiFetch } from "../../utils/apiFetch";
import { vorgangService, type TodoDto, type VorgangDto } from "../../services/vorgangService";
import { useUserLabels } from "../../hooks/useUserLabels";
import { openVorgangCreateFlow } from "../vorgang/create/VorgangCreateFlowWindow";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { TodoListRow, type TodoStatusValue } from "../todo/TodoListRow";
import type { DateRangeValue } from "../redesign/table";
import { BezugsAbschnitt } from "./BezugsAbschnitt";
import { KontaktVorgangTabelle } from "./KontaktVorgangTabelle";
import { EBENEN_TITEL, gruppiereNachBezug } from "./bezugsGruppen";
import {
    LEERE_FILTER,
    anzahlAktiverFilter,
    filtereTodos,
    filtereVorgaenge,
    type FilterWerte,
} from "./vorgangFilter";
import "./kontaktVorgaenge.css";

/**
 * Reiter „Vorgänge" der Kontaktakte (PO 08.09.2026).
 *
 * Drei Änderungen gegenüber der bisherigen Liste:
 *  · Die Vorgänge stehen als TABELLE da, mit denselben Spalten wie die
 *    Vorgangstabelle — nur ohne „Kontakt", der steht ja über der Tabelle.
 *  · Dazu kommen die INDIREKTEN Vorgänge: bei einem Ansprechpartner die seines
 *    Unternehmens, bei einer Firma die des Unternehmensverbunds, bei einer
 *    Privatperson die ihres Haushalts (`include=indirekt`).
 *  · Reihenfolge: erst die eigenen, dann je Herkunftskontakt ein aufklappbarer
 *    Abschnitt „Vorgänge zu <Name>".
 *
 * Bewusst NEBEN `ContactVorgaengeTab` gebaut statt hinein: die alte Liste
 * bedient weiter das Kundentagebuch und die Immobilienvertrag-Seite, wo diese
 * Ansicht erst noch erprobt werden soll.
 *
 * Die Spaltenfilter hält dieser Reiter (nicht die einzelne Tabelle): sie gelten
 * für alle Abschnitte gleichzeitig, sonst zeigte jeder Abschnitt eine andere
 * Auswahl und die Zahlen daneben passten nicht mehr zusammen.
 */

interface Props {
    contactId: number;
    /**
     * `"seite"` (Default) zeichnet den eigenen Kopf mit Überschrift, Zähler,
     * Filter-Reset und „+ Neuer Vorgang". `"eingebettet"` lässt ihn weg — in
     * einer Klapp-Karte liefert der Kartenkopf all das bereits, und zwei
     * Überschriften übereinander sind eine zu viel.
     */
    variant?: "seite" | "eingebettet";
    /** Meldet die Trefferzahl nach dem Laden (Zähler im Kartenkopf). */
    onCountChange?: (anzahl: number) => void;
}

/**
 * Was die Karte von außen auslösen darf — ihr Kartenkopf trägt eingebettet die
 * Knöpfe, die sonst im Kopf dieses Reiters stehen.
 *
 * Achtung: `CollapsibleCard` hängt den Body beim Einklappen aus dem Baum. Das
 * Handle ist deshalb nur bei OFFENER Karte belegt — Aufrufer müssen
 * `ref.current?.…` schreiben und dürfen nicht annehmen, dass das Kind montiert
 * ist.
 */
export type KontaktVorgaengeHandle = { neuLaden: () => void; neuerVorgang: () => void };

export const KontaktVorgaengeReiter = forwardRef<KontaktVorgaengeHandle, Props>(function KontaktVorgaengeReiter(
    { contactId, variant = "seite", onCountChange },
    ref,
) {
    const { openWindow } = useWindowManager();
    const [vorgaenge, setVorgaenge] = useState<VorgangDto[]>([]);
    const [einzelTodos, setEinzelTodos] = useState<TodoDto[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [kontaktName, setKontaktName] = useState(`Kontakt #${contactId}`);
    // Eingeklappte Spalten gelten für ALLE Tabellen des Reiters — sonst stünde
    // in jedem Abschnitt eine andere Spaltenbreite.
    const [eingeklappt, setEingeklappt] = useState<string[]>(["Erstellt"]);
    const [filter, setFilter] = useState<FilterWerte>(LEERE_FILTER);
    const { users } = useUserLabels();

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            // Beides per Kontakt (nicht mandantenweit): die Endpunkte prüfen die
            // Kontakt-RBAC bereits, ein fremder Kontakt endet in 403.
            const [alle, verknuepfteTodos] = await Promise.all([
                vorgangService.getVorgaengeByContact(contactId, { indirekt: true, force: true }),
                vorgangService.getTodosByLinked("KUNDE", contactId, { force: true }),
            ]);

            // To-dos, die schon als Kindzeile eines geladenen Vorgangs hängen,
            // nicht zusätzlich unter „Weitere Vorgänge" doppeln.
            const inVorgaengen = new Set<number>(alle.flatMap((v) => (v.todos ?? []).map((t) => t.id)));
            setVorgaenge(alle);
            setEinzelTodos(
                verknuepfteTodos
                    .filter((t) => !inVorgaengen.has(t.id))
                    .sort((a, b) => Number(a.status === "Erledigt") - Number(b.status === "Erledigt")),
            );
        } catch (err: any) {
            setFehler(err?.message ?? "Fehler beim Laden");
        } finally {
            setLaedt(false);
        }
    }, [contactId]);

    useEffect(() => {
        void laden();
    }, [laden]);

    // Name für den Anlege-Flow („Neuer Vorgang" ist auf den Kontakt vorbelegt).
    useEffect(() => {
        let abgebrochen = false;
        apiFetch<any>(`contact/${contactId}`)
            .then(({ data }) => {
                if (abgebrochen || !data) return;
                setKontaktName(
                    data.displayName ||
                        [data.lastname, data.firstName].filter(Boolean).join(", ") ||
                        data.companyName ||
                        `Kontakt #${contactId}`,
                );
            })
            .catch(() => undefined);
        return () => {
            abgebrochen = true;
        };
    }, [contactId]);

    // Erst filtern, dann gruppieren: so verschwinden leer gefilterte Abschnitte
    // von selbst und jeder Zähler nennt das, was darunter steht.
    const gefiltert = useMemo(() => filtereVorgaenge(vorgaenge, filter, users), [vorgaenge, filter, users]);
    const sichtbareTodos = useMemo(() => filtereTodos(einzelTodos, filter, users), [einzelTodos, filter, users]);
    const { eigen, gruppen } = useMemo(() => gruppiereNachBezug(gefiltert), [gefiltert]);

    const aktiveFilter = anzahlAktiverFilter(filter);

    // Ein gelöschter Filter fliegt aus dem Objekt, statt als `undefined`
    // stehen zu bleiben — sonst zählte `anzahlAktiverFilter` leere Einträge mit.
    const textFilter = useCallback((id: string, wert: string | boolean | undefined) => {
        setFilter((prev) => {
            const text = { ...prev.text };
            if (wert === undefined || wert === "" || wert === false) delete text[id];
            else text[id] = wert;
            return { ...prev, text };
        });
    }, []);
    const datumFilter = useCallback((id: string, wert: DateRangeValue) => {
        setFilter((prev) => {
            const datum = { ...prev.datum };
            if (!wert?.von && !wert?.bis) delete datum[id];
            else datum[id] = wert;
            return { ...prev, datum };
        });
    }, []);
    const personenFilter = useCallback((id: string, wert: string[]) => {
        setFilter((prev) => {
            const personen = { ...prev.personen };
            if (wert.length === 0) delete personen[id];
            else personen[id] = wert;
            return { ...prev, personen };
        });
    }, []);

    const spalteUmschalten = useCallback(
        (id: string) =>
            setEingeklappt((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
        [],
    );

    const statusAendern = async (todo: TodoDto, next: TodoStatusValue) => {
        try {
            await vorgangService.updateTodoStatus(todo.id, next);
            await laden();
        } catch (err) {
            console.error("[KontaktVorgaengeReiter] Status ändern fehlgeschlagen", err);
        }
    };

    const anlegen = useCallback(
        () =>
            openVorgangCreateFlow(openWindow, {
                initialContactId: contactId,
                initialContactName: kontaktName,
            }),
        [openWindow, contactId, kontaktName],
    );

    useImperativeHandle(ref, () => ({ neuLaden: () => void laden(), neuerVorgang: anlegen }), [laden, anlegen]);

    // Der Zähler nennt die Vorgänge DIESES Kontakts — indirekte gehören
    // jemand anderem und würden die Zahl aufblähen.
    const anzahl = eigen.length + sichtbareTodos.length;

    // Erst melden, wenn wirklich geladen wurde: eine 0 während des Ladens
    // erschiene im Kartenkopf und würde eine Sekunde später korrigiert.
    useEffect(() => {
        if (laedt) return;
        onCountChange?.(anzahl);
    }, [laedt, anzahl, onCountChange]);

    let letzteEbene: string | null = null;

    return (
        <div className="kvg-wrap">
            {variant === "seite" && (
                <div className="kvg-kopf">
                    <h3>Vorgänge</h3>
                    <span className="kvg-zahl">{anzahl}</span>
                    <span className="kvg-kopf-akt">
                        {aktiveFilter > 0 && (
                            <button type="button" className="tbtn" onClick={() => setFilter(LEERE_FILTER)}>
                                Filter zurücksetzen ({aktiveFilter})
                            </button>
                        )}
                        <button type="button" className="tbtn" onClick={() => void laden()} title="Aktualisieren">
                            ↻
                        </button>
                        <button type="button" className="tbtn primary" onClick={anlegen}>
                            + Neuer Vorgang
                        </button>
                    </span>
                </div>
            )}

            {/* Eingebettet fällt der Kopf weg — das Zurücksetzen darf aber nicht
                mit ihm verschwinden, sonst bliebe ein gesetzter Spaltenfilter
                in der Karte hängen, ohne Weg zurück. */}
            {variant === "eingebettet" && aktiveFilter > 0 && (
                <div className="kvg-kopf-akt kvg-kopf-akt--eingebettet">
                    <button type="button" className="tbtn" onClick={() => setFilter(LEERE_FILTER)}>
                        Filter zurücksetzen ({aktiveFilter})
                    </button>
                </div>
            )}

            {laedt && (
                <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                    <Spinner size="tiny" label="Vorgänge werden geladen…" />
                </div>
            )}

            {fehler && (
                <div className="kvg-fehler">
                    <p>{fehler}</p>
                    <button type="button" className="tbtn" onClick={() => void laden()}>
                        Erneut versuchen
                    </button>
                </div>
            )}

            {!laedt && !fehler && (
                <>
                    <KontaktVorgangTabelle
                        vorgaenge={eigen}
                        eingeklappt={eingeklappt}
                        onToggleSpalte={spalteUmschalten}
                        filter={filter}
                        onFilterChange={textFilter}
                        onDatumChange={datumFilter}
                        onPersonenChange={personenFilter}
                        leerText={aktiveFilter > 0 ? "Kein Vorgang passt zum Filter." : "Keine eigenen Vorgänge."}
                    />

                    {gruppen.map((g) => {
                        const ebeneTitel = g.ebene === letzteEbene ? undefined : EBENEN_TITEL[g.ebene];
                        letzteEbene = g.ebene;
                        return (
                            <BezugsAbschnitt
                                key={`${g.ebene}-${g.kontaktId}`}
                                kontaktName={g.kontaktName}
                                anzahl={g.vorgaenge.length}
                                ebeneTitel={ebeneTitel}
                                /* Der Verbund kann bei einem Konzern zwei
                                   Dutzend Abschnitte haben — offen wäre das
                                   eigene Unternehmen darüber nicht mehr zu
                                   finden (siehe `BezugsAbschnitt`). */
                                startZu={g.ebene === "verbund"}
                            >
                                <KontaktVorgangTabelle
                                    vorgaenge={g.vorgaenge}
                                    eingeklappt={eingeklappt}
                                    onToggleSpalte={spalteUmschalten}
                                    filter={filter}
                                    onFilterChange={textFilter}
                                    onDatumChange={datumFilter}
                                    onPersonenChange={personenFilter}
                                />
                            </BezugsAbschnitt>
                        );
                    })}

                    {/* „Weitere Vorgänge" = KUNDE-verknüpfte To-dos, die unter keinem
                        der geladenen Vorgänge hängen. Sie navigieren bewusst NICHT:
                        ihr Vorgang gehört einem anderen Kunden, der Sprung führte aus
                        der Akte heraus (und bei fehlender Sichtbarkeit in ein 404). */}
                    {sichtbareTodos.length > 0 && (
                        <div className="kvg-abschnitt">
                            <span className="kvg-ebene">Weitere Vorgänge</span>
                            <div className="card">
                                {sichtbareTodos.map((t) => (
                                    <TodoListRow key={t.id} todo={t} onStatusChange={statusAendern} />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

export default KontaktVorgaengeReiter;
